import { Globe } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { SUPPORTED_LOCALES, type SupportedLocale } from "../i18n";
import { cn } from "../lib/utils";

/**
 * Compact language picker - a globe icon that opens a list of the
 * supported locales. The user's pick lands in
 * ``localStorage['tilt-pos.lang']`` (configured in i18n.ts) so it
 * survives a tab refresh and applies to subsequent sessions on the
 * same browser.
 *
 * Localized label is intentionally identical across locales (each
 * locale's name is the autonym in its own script - "Español",
 * "Français") because that's the convention that's friendly to a
 * user who landed on the wrong language and is trying to switch
 * away. The bare "Globe" icon labels itself with the active
 * autonym so screen-reader users hear the active language name.
 */
export function LanguagePicker() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const active = (i18n.resolvedLanguage || "en") as SupportedLocale;

  function pick(loc: SupportedLocale) {
    void i18n.changeLanguage(loc);
    setOpen(false);
  }

  // WCAG 1.4.13: Escape must dismiss the dropdown and return focus to trigger.
  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t("language.label")}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-9 items-center gap-1.5 rounded-full bg-surface-strong px-3 text-xs font-medium text-foreground hover:bg-surface-strong"
      >
        <Globe className="h-4 w-4" />
        <span>{t(`language.${active}`)}</span>
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-30 mt-2 w-44 overflow-hidden rounded-md border border-line bg-surface py-1 shadow-lg"
        >
          {SUPPORTED_LOCALES.map((loc) => (
            <li key={loc}>
              <button
                type="button"
                onClick={() => pick(loc)}
                className={cn(
                  "block w-full px-3 py-2 text-left text-sm hover:bg-surface-muted",
                  loc === active ? "font-semibold text-strong" : "text-foreground",
                )}
              >
                {t(`language.${loc}`)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
